import React from "react";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "@/components/icons";
import { changePassword } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

/**
 * Self-service "Change password" for the signed-in user.
 *
 * Mirrors the mobile app: a logged-in user sets a NEW password directly, no
 * recovery link/code and no current password required (the session already
 * proves identity). This is distinct from "Reset password" (send a recovery
 * link/code), which is for when you DON'T know the current password.
 *
 * Unlike the mobile app, this always gives an explicit confirmation on success.
 *
 * The password minimum below is only a client-side HINT so the field can show a
 * helpful message before submit; the backend policy (web/password_policy.py) is
 * authoritative and rejects anything shorter.
 */
export function minPasswordLength(role?: string | null): number {
  return role === "admin" ? 18 : 10;
}

export function ChangePasswordDialog({
  open,
  onClose,
  userId,
  role,
}: {
  open: boolean;
  onClose: () => void;
  userId: number;
  role?: string | null;
}) {
  const t = useT();
  const [next, setNext] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [showNext, setShowNext] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const minLen = minPasswordLength(role);

  // Reset fields whenever the dialog opens so a previous attempt never leaks in.
  React.useEffect(() => {
    if (open) {
      setNext("");
      setConfirm("");
      setShowNext(false);
      setShowConfirm(false);
      setSaving(false);
    }
  }, [open]);

  const tooShort = next.length > 0 && next.length < minLen;
  const mismatch = confirm.length > 0 && next !== confirm;
  const valid = next.length >= minLen && next === confirm;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      await changePassword(userId, next);
      toast.success(t("operator.auth.change_password_done"));
      onClose();
    } catch (err: any) {
      toast.error(errorMessage(t, err, { fallback: "operator.auth.change_password_failed" }));
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t("operator.auth.change_password_title")}>
      <form className="space-y-4" onSubmit={submit}>
        <div className="space-y-1.5">
          <Label htmlFor="change-password-new">{t("operator.auth.new_password")}</Label>
          <div className="relative">
            <Input
              id="change-password-new"
              className="pr-10"
              type={showNext ? "text" : "password"}
              autoComplete="new-password"
              autoFocus
              value={next}
              disabled={saving}
              onChange={(e) => setNext(e.target.value)}
              aria-invalid={tooShort || undefined}
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={showNext ? t("operator.auth.hide_password") : t("operator.auth.show_password")}
              onClick={() => setShowNext((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              {showNext ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="px-1 text-xs opacity-60">
            {t("operator.auth.change_password_min", { min: minLen })}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="change-password-confirm">{t("operator.auth.confirm_new_password")}</Label>
          <div className="relative">
            <Input
              id="change-password-confirm"
              className="pr-10"
              type={showConfirm ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              disabled={saving}
              onChange={(e) => setConfirm(e.target.value)}
              aria-invalid={mismatch || undefined}
            />
            <button
              type="button"
              tabIndex={-1}
              aria-label={showConfirm ? t("operator.auth.hide_password") : t("operator.auth.show_password")}
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {mismatch && (
            <p className="px-1 text-xs text-destructive">{t("operator.auth.change_password_mismatch")}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>
            {t("common.action.cancel")}
          </Button>
          <Button type="submit" disabled={!valid || saving}>
            {saving ? t("operator.auth.changing_password") : t("operator.auth.change_password_submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
